import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatCard } from "@/components/PageHeader";
import { api, errMsg, money } from "@/lib/api";
import { fmtDay, fmtTime, ymd } from "@/lib/dates";

const niceDate = (d) => format(parseISO(d), "d MMM yyyy");

export default function Timesheets() {
  const [on, setOn] = useState(() => ymd(new Date()));
  const [period, setPeriod] = useState(null);
  const [rows, setRows] = useState([]);
  const [exporting, setExporting] = useState(false);
  const load = useCallback(async () => {
    const { data: per } = await api.get("/biz/pay-period", { params: { on } });
    setPeriod(per);
    const { data } = await api.get("/biz/timesheets", { params: { start: per.start, end: per.end } });
    setRows(data);
  }, [on]);
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/biz/timesheets/export", { params: { start: period.start, end: period.end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll_${period.start}_to_${period.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Payroll CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };
  const approvedCount = rows.filter((r) => r.approved && r.clock_out).length;

  const totals = useMemo(() => rows.filter((e) => e.approved).reduce((a, e) => ({ hours: a.hours + (e.paid_hours || 0), allowance: a.allowance + (e.allowance || 0), pay: a.pay + (e.pay || 0) }), { hours: 0, allowance: 0, pay: 0 }), [rows]);

  const approve = async (id, approved) => {
    try {
      await api.put(`/biz/time-entries/${id}`, { approved });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Timesheets" subtitle={period ? `${period.frequency_label} pay period: ${niceDate(period.start)} – ${niceDate(period.end)}${period.estimated ? " (estimated — add more dates in Settings)" : ""}` : ""}>
        <Button variant="outline" size="icon" disabled={!period} onClick={() => setOn(ymd(addDays(parseISO(period.start), -1)))} data-testid="ts-prev-period-btn"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setOn(ymd(new Date()))} data-testid="ts-current-period-btn">Current period</Button>
        <Button variant="outline" size="icon" disabled={!period} onClick={() => setOn(ymd(addDays(parseISO(period.end), 1)))} data-testid="ts-next-period-btn"><ChevronRight className="h-4 w-4" /></Button>
        <Button className="rounded-full" disabled={!period || exporting || approvedCount === 0} onClick={exportCsv} data-testid="ts-export-csv-btn">
          <Download className="mr-1 h-4 w-4" />{exporting ? "Exporting…" : `Export payroll CSV (${approvedCount})`}
        </Button>
      </PageHeader>
      <p className="-mt-4 mb-6 text-xs text-slate-500">Only approved entries are included in the payroll export. Change your pay period in Settings.</p>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Approved paid hours" value={totals.hours.toFixed(2)} testId="ts-total-hours" />
        <StatCard label="Approved sleepover allowances" value={money(totals.allowance)} testId="ts-total-allowance" accent="text-purple-600" />
        <StatCard label="Approved gross pay" value={money(totals.pay)} testId="ts-total-pay" accent="text-indigo-600" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Staff</TableHead><TableHead>Clock in</TableHead><TableHead>Clock out</TableHead><TableHead>Scheduled</TableHead>
            <TableHead>Paid hrs</TableHead><TableHead>Allowance</TableHead><TableHead>Pay</TableHead><TableHead>Approved</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-500">No clock-ins in this pay period.</TableCell></TableRow>}
            {rows.map((e) => {
              const diff = e.scheduled_hours != null && e.paid_hours != null ? e.paid_hours - e.scheduled_hours : null;
              return (
                <TableRow key={e.id} data-testid={`ts-row-${e.id}`}>
                  <TableCell className="font-semibold">{e.staff_name}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtDay(e.clock_in)} {fmtTime(e.clock_in)}</TableCell>
                  <TableCell className="font-mono text-sm">{e.clock_out ? `${fmtDay(e.clock_out)} ${fmtTime(e.clock_out)}` : <span className="text-emerald-600">On shift</span>}</TableCell>
                  <TableCell className="font-mono text-sm">{e.shift ? `${fmtTime(e.shift.start)}–${fmtTime(e.shift.end)}` : "Unscheduled"}</TableCell>
                  <TableCell className="font-mono">{e.paid_hours?.toFixed(2) ?? "—"}{diff != null && Math.abs(diff) > 0.25 && <span className={`ml-1 text-xs ${diff > 0 ? "text-amber-600" : "text-rose-600"}`}>({diff > 0 ? "+" : ""}{diff.toFixed(1)})</span>}</TableCell>
                  <TableCell className="font-mono">{e.allowance ? money(e.allowance) : "—"}</TableCell>
                  <TableCell className="font-mono font-semibold">{e.pay != null ? money(e.pay) : "—"}</TableCell>
                  <TableCell><Switch checked={!!e.approved} disabled={!e.clock_out} onCheckedChange={(v) => approve(e.id, v)} data-testid={`ts-approve-switch-${e.id}`} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
